"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import {
  type Participant,
  suggestionToParticipant,
  TeamCard,
} from "./base-match-template";
import { ActivityDisplayPreview } from "./previews";
import { VolleyballScoreEditor } from "./score-editors/volleyball-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type VolleyballTemplateData = {
  matchScore: { yourSets: number; opponentSets: number };
  sets?: Array<{ yourScore: number; opponentScore: number }>;
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type VolleyballParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type VolleyballTemplateProps = {
  currentUser: VolleyballParticipant;
  onChange: (
    data: VolleyballTemplateData,
    participants: VolleyballParticipant[],
  ) => void;
  initialData?: Partial<VolleyballTemplateData>;
};

export function VolleyballTemplate({
  currentUser,
  onChange,
  initialData,
}: VolleyballTemplateProps) {
  const [participants, setParticipants] = useState<VolleyballParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type VolleyballScorePart = Pick<
    VolleyballTemplateData,
    "matchScore" | "sets"
  >;
  const [scorePart, setScorePart] = useState<VolleyballScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourSets: 0, opponentSets: 0 },
    sets: initialData?.sets ?? [],
  }));

  // Flexible team sizes (allows casual games and subs)
  const maxPerTeam = 15;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    const data: VolleyballTemplateData = {
      matchScore: scorePart.matchScore,
      sets: scorePart.sets ?? [],
      positions: teams,
      hasScore: true,
    };
    onChange(data, participants);
  }, [scorePart, teams, participants, onChange]);

  const yourSets = scorePart.matchScore.yourSets;
  const opponentSets = scorePart.matchScore.opponentSets;

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    if (teamCount >= maxPerTeam) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourWon = yourSets > opponentSets;
  const opponentWon = opponentSets > yourSets;
  const hasOpponent = opponentTeam.length > 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
        />
      </div>

      {!hasOpponent && (
        <div className="rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning">
          <span>Add at least one opponent to post this match</span>
        </div>
      )}

      <VolleyballScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) =>
          setScorePart(data as unknown as VolleyballScorePart)
        }
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type VolleyballDisplayProps = {
  data: VolleyballTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function VolleyballDisplay({
  data,
  participants,
}: VolleyballDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="volleyball"
      data={data}
      participants={participants}
    />
  );
}
